import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
  Link,
} from '@mui/material';
import ProcessWorkflow from './ProcessNavigation';
import {useDispatch, useSelector} from 'react-redux';
//import {selectUserProfile} from 'app/store/auth/authSlice';
import * as React from 'react';
import {
  OnboardingActions,
  selectOnboarding,
  selectRoleFilters,
  selectSelectedGroup,
  setOnboarding,
  setSelectedGroup,
} from 'app/store/appState/appStateSlice';
import {selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';
import {useFeatureFlag} from 'configcat-react';
import {UserFilled, WarningAltFilled} from '@carbon/icons-react';
import {ChangePageType, E_QuickLinks, IProcess, LogInType, QuickLinks} from 'app/types/Shared';
import {selectUserType} from 'app/store/users/userSlice';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import ProcessUpdates from 'components/ProcessUpdates/ProcessUpdates';
import LinksGrid from 'components/LinksGrid/LinksGrid';
import TDOMLinks from 'components/LinksGrid/TDOMLinks.json';

type homeProps = {
  processClickCallback: (process: IProcess) => void;
};

const HomeProcessNavigation = ({processClickCallback}: homeProps) => {
  const theme = useTheme();
  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);
  const onboarding = useSelector(selectOnboarding);
  const allProcesses = useSelector(selectAllProcesses);
  const selectedRoles = useSelector(selectRoleFilters);
  const selectedGroup = useSelector(selectSelectedGroup);
  const userType = useSelector(selectUserType);
  const dispatch = useDispatch();
  const {trackEvent} = useMatomo();

  const tooltipTDOM = (
    <React.Fragment>
      <Typography variant='body2'>
        {"If you're not sure, ask your manager or visit the "}
        <Link target='_blank' href='https://mytakeda.sharepoint.com/sites/RDFutureFit'>
          Future Fit information center
        </Link>
      </Typography>
    </React.Fragment>
  );

  const groups = allProcesses?.reduce((acc, curr) => {
    if (!acc.includes(curr.category_group) && curr.category_group) {
      acc.push(curr.category_group);
    }
    return acc;
  }, [] as Array<string>);

  React.useEffect(() => {
    if (!selectedGroup) {
      const firstGroup = allProcesses?.find((x) => x.category_group_order === 1)?.category_group;
      if (firstGroup) {
        dispatch(setSelectedGroup(firstGroup));
      }
    }
  }, [selectedGroup, allProcesses, dispatch]);

  const changeProcessClick = (process_id: string) => {
    const process = allProcesses?.find((x) => x.process_id === process_id);

    if (process) {
      processClickCallback(process);
    }
  };

  const containsProcessesRole = (group: string) => {
    let flag = false;
    const processesByGroup = allProcesses?.filter((x) => x.category_group === group);
    const hasRole = processesByGroup?.some((process) => {
      process.roles.map((processRoles) => {
        if (selectedRoles.some((role) => role === processRoles.role.role_category.label)) {
          flag = true;
        }
      });
      return flag;
    });

    return hasRole;
  };

  //Responsive breakpoint bases oveflow on veiwport of landscape tablet (1150px breakpoint)
  const [width, setWidth] = React.useState(window.innerWidth);
  const breakpoint = 1000;
  React.useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    // subscribe to window resize event "onComponentDidMount"
    window.addEventListener('resize', handleResizeWindow);
    return () => {
      // unsubscribe "onComponentDestroy"
      window.removeEventListener('resize', handleResizeWindow);
    };
  }, []);

  const selectedGroupInQuickLinks = () => {
    return QuickLinks.includes(selectedGroup);
  };

  //render selected quicklink content
  const SelectedQuickLink = (selected: string) => {
    switch (selected) {
      case E_QuickLinks.Updates:
        return (
          <ProcessUpdates
            type={ChangePageType.HomePage}
            title='Process Updates'
            subtitle='Recent Updates to Processes'
            processNavigation={changeProcessClick}
          />
        );
      case E_QuickLinks.TDOM:
        return <LinksGrid resourceGroups={TDOMLinks} />;
      default:
        return <></>;
    }
  };

  //Control which quicklinks are displayed based on feature flags and user type
  const renderQuickLink = (link: string) => {
    switch (link) {
      case E_QuickLinks.TDOM:
        return true;
      case E_QuickLinks.Updates:
        return isChangeNotificationsEnabled;
      default:
        return false;
    }
  };

  const onProcessGroupSelected = (item: string) => {
    trackEvent({category: 'Navigation', action: 'Functional Area Selected', name: item});
    dispatch(setSelectedGroup(item));
    if (onboarding?.roleGuide) {
      dispatch(setOnboarding(OnboardingActions.DisableRoleGuide));
    }
  };

  const QuicklinksToolTip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 12,
      maxWidth: '220px',
    },
  });

  type groupButtonProps = {
    item: string;
  };
  const GroupButton = ({item}: groupButtonProps) => {
    const wordBreak = () => {
      const len = item?.length;
      const hasRole = containsProcessesRole(item);
      if (len > 25 && hasRole) {
        return true;
      } else if (len > 20 && width < 1300 && hasRole) {
        return true;
      } else if (width < 1210 && len > 19 && hasRole) {
        return true;
      } else return false;
    };

    function splitString(inputString: string) {
      // Find the last space in the string
      const lastSpaceIndex = inputString.lastIndexOf(' ');

      // If no space found, return original string and an empty string
      if (lastSpaceIndex === -1) {
        return [inputString, ''];
      }

      // Split the string into two parts at the last space
      const firstPart = inputString.substring(0, lastSpaceIndex);
      const secondPart = inputString.substring(lastSpaceIndex + 1);

      return [firstPart, secondPart];
    }

    return (
      <Button
        sx={{
          minHeight: '40px',
          maxHeight: '40px',
          color: selectedGroup === item ? 'white' : 'grey.800',
          bgcolor: selectedGroup === item ? theme.palette.primary.main : theme.palette.grey[400],
          padding: 0,
          px: 1,
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          '&:hover': {
            bgcolor: theme.palette.grey[500],
            color: theme.palette.grey[700],
            cursor: 'pointer',
          },
        }}
        onClick={() => {
          onProcessGroupSelected(item);
        }}
      >
        <Stack spacing='10px' direction='row' display='flex' alignItems='center'>
          {!wordBreak() ? (
            <Typography variant='h3' fontWeight={400} fontSize='14px' textAlign='left'>
              {item}{' '}
            </Typography>
          ) : (
            <Stack>
              {splitString(item).map((i) => (
                <Typography key={i} variant='h3' fontWeight={400} fontSize='14px' textAlign='left'>
                  {i}
                </Typography>
              ))}
            </Stack>
          )}{' '}
          {containsProcessesRole(item) && (
            <UserFilled height='18px' width='18px' style={{color: selectedGroup === item ? 'white' : 'grey.800'}} />
          )}
        </Stack>
      </Button>
    );
  };

  return (
    <Box sx={{overflowY: width > breakpoint ? 'unset' : 'auto'}}>
      <Box px={1} minWidth={1024}>
        <Box
          sx={{
            backgroundColor: theme.palette.grey[400],
            minHeight: 626,
          }}
        >
          <Grid container direction='row' justifyContent={'flex-start'} sx={{minHeight: 626}} columnSpacing={2}>
            <Grid
              item
              xs={2.4}
              justifyContent='flex-start'
              alignItems='flex-start'
              mb={1}
              display='flex'
              minWidth={200}
              maxHeight={600}
              sx={{borderRight: `1px solid ${theme.palette.grey[500]}`}}
            >
              <Box bgcolor={theme.palette.grey[400]} pr={2} py={1} minHeight={150} minWidth={200} width='100%'>
                <Stack direction='column' justifyContent='flex-start' spacing={2} pt={2}>
                  <Typography fontWeight={600} fontSize={12}>
                    PROCESSES BY TRIAL DELIVERY FOCUS
                  </Typography>

                  <Box>
                    <Stack spacing={1}>
                      {groups?.map((item, index) => (
                        <Stack spacing={1} direction='row' key={item + '-' + index}>
                          <Box
                            height='40px'
                            width='4px'
                            bgcolor={selectedGroup === item ? theme.palette.primary.main : theme.palette.grey[400]}
                          />
                          <GroupButton item={item} />
                        </Stack>
                      ))}
                      <Stack spacing={1} direction='row'>
                        <Box
                          sx={{
                            padding: 0,
                            px: 1,
                            justifyContent: 'flex-start',
                            width: '100%',
                            borderBottom: `1px solid ${theme.palette.grey[500]}`,
                          }}
                        />
                      </Stack>
                      <Stack spacing={1} direction='column'>
                        {QuickLinks?.filter((link) => {
                          return renderQuickLink(link);
                        }).map((item, index) => (
                          <Stack spacing={1} direction='column' key={item + '-border-' + index}>
                            <Stack spacing={1} direction='row' key={item + '-' + index}>
                              <Box
                                height='40px'
                                width='4px'
                                bgcolor={selectedGroup === item ? theme.palette.primary.main : theme.palette.grey[400]}
                              />
                              <QuicklinksToolTip
                                placement='bottom-end'
                                title={item === E_QuickLinks.TDOM && userType === LogInType.Internal && tooltipTDOM}
                              >
                                <Button
                                  key={item + '-' + index}
                                  sx={{
                                    minHeight: '40px',
                                    maxHeight: '40px',
                                    color: selectedGroup === item ? 'white' : 'grey.800',
                                    bgcolor:
                                      selectedGroup === item ? theme.palette.primary.main : theme.palette.grey[400],
                                    padding: 0,
                                    px: 1,
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    width: '100%',
                                    '&:hover': {
                                      bgcolor: theme.palette.grey[500],
                                      color: theme.palette.grey[700],
                                      cursor: 'pointer',
                                    },
                                  }}
                                  onClick={() => {
                                    onProcessGroupSelected(item);
                                  }}
                                >
                                  <Stack textAlign='left' direction='row' spacing='10px' alignItems='center'>
                                    <Typography variant='h3' fontWeight={400} fontSize='14px' textAlign='left'>
                                      {item}
                                    </Typography>
                                    {item === E_QuickLinks.Updates && (
                                      <WarningAltFilled
                                        height='18px'
                                        width='18px'
                                        style={{color: selectedGroup === item ? 'white' : theme.palette.grey[800]}}
                                      />
                                    )}
                                  </Stack>
                                </Button>
                              </QuicklinksToolTip>
                            </Stack>
                            {index !==
                              QuickLinks?.filter((link) => {
                                return renderQuickLink(link);
                              }).length -
                                1 && (
                              <Box
                                sx={{
                                  padding: 0,
                                  px: 1,
                                  justifyContent: 'flex-start',
                                  width: '100%',
                                  borderBottom: `1px solid ${theme.palette.grey[500]}`,
                                }}
                              />
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={9.6} pb={2}>
              {!selectedGroupInQuickLinks() ? (
                <ProcessWorkflow selectedGroup={selectedGroup} processClickCallback={processClickCallback} />
              ) : (
                SelectedQuickLink(selectedGroup)
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeProcessNavigation;
